"use client";

import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
} from "@heroui/react";
import {
  HandThumbsUp,
  HandThumbsDown,
  Send,
  StarFill,
  Heart,
} from "react-bootstrap-icons";

import { useZodForm, getFieldProps } from "@/lib/form-utils";
import {
  FeedbackModalFormSchema,
  FeedbackModalFormData,
} from "@/lib/form-schemas";
import { logger } from "@/lib/secure-logger";

interface FeedbackModalProps {
  isOpen: boolean;
  onCloseAction: () => void;
  initialRating?: "positive" | "negative" | null;
  userJourney?: {
    answers?: Record<string, string | string[]>;
    currentQuestionId?: string | null;
    progressPercent?: number;
  };
}

export function FeedbackModal({
  isOpen,
  onCloseAction,
  initialRating,
  userJourney,
}: FeedbackModalProps) {
  const [selectedRating, setSelectedRating] = useState<
    "positive" | "negative" | null
  >(initialRating || null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);

  // Reset rating when initialRating changes
  useEffect(() => {
    if (initialRating) {
      setSelectedRating(initialRating);
    }
  }, [initialRating]);

  // Suggestions based on feedback type
  const positiveSuggestions = [
    "Easy to understand",
    "Fast and efficient",
    "Good design",
    "Helpful recommendations",
    "Clear navigation",
    "Relevant content",
    "Good onboarding flow",
    "Saves time",
  ];

  const negativeSuggestions = [
    "Too complex",
    "Slow to load",
    "Confusing interface",
    "Irrelevant suggestions",
    "Hard to navigate",
    "Missing features",
    "Technical issues",
    "Too many steps",
    "Poor mobile experience",
    "Unclear instructions",
  ];

  const form = useZodForm(FeedbackModalFormSchema, {
    defaultValues: {
      additionalComments: "",
    },
  });

  const handleRatingSelect = async (rating: "positive" | "negative") => {
    setSelectedRating(rating);
    // Reset suggestions when rating changes
    setSelectedSuggestions([]);

    // Send feedback phase 1 immediately when user clicks thumbs
    try {
      const feedbackData = {
        phase1: rating,
        sessionId:
          localStorage.getItem("crowdsec_session_id") ||
          `session_${Date.now()}`,
        timestamp: new Date().toISOString(),
        userJourney: {
          answers: userJourney?.answers || {},
          currentQuestionId: userJourney?.currentQuestionId || null,
          progressPercent: userJourney?.progressPercent || 0,
        },
      };

      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(feedbackData),
      });

      if (response.ok) {
        logger.info("Phase 1 feedback sent immediately", {
          rating,
          source: "modal_thumbs",
        });
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      logger.error("Phase 1 feedback API error", { error, rating });
      // Continue with modal flow even if API fails
    }
  };

  const toggleSuggestion = (suggestion: string) => {
    setSelectedSuggestions((prev) =>
      prev.includes(suggestion)
        ? prev.filter((s) => s !== suggestion)
        : [...prev, suggestion],
    );
  };

  const handleSubmit = async (data: FeedbackModalFormData) => {
    if (!selectedRating) return;

    try {
      // Send phase 2 feedback (suggestions + comments) as update
      const feedbackData = {
        phase1: selectedRating, // Include for reference
        phase2: {
          whatWorked: selectedRating === "positive" ? selectedSuggestions : [],
          whatDidntWork:
            selectedRating === "negative" ? selectedSuggestions : [],
          additionalComments: data.additionalComments?.trim() || "",
        },
        sessionId:
          localStorage.getItem("crowdsec_session_id") ||
          `session_${Date.now()}`,
        timestamp: new Date().toISOString(),
        userJourney: {
          answers: userJourney?.answers || {},
          currentQuestionId: userJourney?.currentQuestionId || null,
          progressPercent: userJourney?.progressPercent || 0,
        },
      };

      // Send complete feedback with phase2 data
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Log success
      logger.info("Phase 2 feedback submitted to API", {
        feedbackId: result.feedbackId,
        rating: selectedRating,
        suggestionsCount: selectedSuggestions.length,
        hasComments: !!data.additionalComments?.trim(),
      });

      setIsSubmitted(true);

      // Auto close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      logger.error("Phase 2 feedback submission error", { error });

      // Still show success to user even if there's an error
      // (graceful degradation)
      setIsSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 3000);
    }
  };

  const handleClose = () => {
    setSelectedRating(null);
    setSelectedSuggestions([]);
    setIsSubmitted(false);
    form.reset();
    onCloseAction();
  };

  const commentsProps = getFieldProps(form, "additionalComments");

  return (
    <Modal
      backdrop="blur"
      isOpen={isOpen}
      placement="center"
      size="2xl"
      onClose={handleClose}
    >
      <ModalContent className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-600/30">
        {!isSubmitted ? (
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <ModalHeader className="flex flex-col gap-1 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    How was your experience?
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Your feedback shapes our product - every comment is read and
                    valued!
                  </p>
                </div>
              </div>
            </ModalHeader>

            <ModalBody className="py-6">
              <div className="space-y-6">
                {/* Rating selection */}
                <div className="text-center">
                  <p className="text-gray-300 mb-6 text-lg">
                    Your experience matters to us - help us build better
                    security tools!
                  </p>
                  <div className="flex justify-center gap-6">
                    <Button
                      className={`group relative transition-all duration-300 transform hover:scale-105 ${
                        selectedRating === "positive"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-xl shadow-green-600/30 scale-105"
                          : "bg-slate-700/50 hover:bg-slate-600/50 text-gray-300 hover:text-white border border-slate-600/30 hover:border-slate-500/50"
                      } font-semibold py-6 px-10 rounded-xl flex items-center gap-3 min-w-[180px]`}
                      size="lg"
                      onPress={() => handleRatingSelect("positive")}
                    >
                      <div className="relative">
                        <HandThumbsUp className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
                        {selectedRating === "positive" && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-300 rounded-full animate-ping" />
                        )}
                      </div>
                      <span>Love it! 👍</span>
                    </Button>

                    <Button
                      className={`group relative transition-all duration-300 transform hover:scale-105 ${
                        selectedRating === "negative"
                          ? "bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-xl shadow-orange-600/30 scale-105"
                          : "bg-slate-700/50 hover:bg-slate-600/50 text-gray-300 hover:text-white border border-slate-600/30 hover:border-slate-500/50"
                      } font-semibold py-6 px-10 rounded-xl flex items-center gap-3 min-w-[180px]`}
                      size="lg"
                      onPress={() => handleRatingSelect("negative")}
                    >
                      <div className="relative">
                        <HandThumbsDown className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
                        {selectedRating === "negative" && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-300 rounded-full animate-ping" />
                        )}
                      </div>
                      <span>Needs work 👎</span>
                    </Button>
                  </div>
                </div>

                {/* Additional comments */}
                {selectedRating && (
                  <div className="animate-fadeIn space-y-6">
                    {/* Suggestions */}
                    <div>
                      <h3 className="text-white font-medium mb-3">
                        {selectedRating === "positive"
                          ? "What made it great? (Select all that apply)"
                          : "What needs improvement? (Select all that apply)"}
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        {(selectedRating === "positive"
                          ? positiveSuggestions
                          : negativeSuggestions
                        ).map((suggestion) => (
                          <Button
                            key={suggestion}
                            className={`text-left justify-start py-2 px-3 text-sm rounded-lg transition-all duration-200 border ${
                              selectedSuggestions.includes(suggestion)
                                ? selectedRating === "positive"
                                  ? "bg-success/20 border-success/50 text-success shadow-lg shadow-success/10"
                                  : "bg-warning/20 border-warning/50 text-warning shadow-lg shadow-warning/10"
                                : "bg-slate-700/50 border-slate-600/30 text-gray-300 hover:bg-slate-600/50 hover:border-slate-500/50 hover:text-white"
                            }`}
                            variant="flat"
                            onPress={() => toggleSuggestion(suggestion)}
                          >
                            <span className="flex items-center gap-2">
                              {selectedSuggestions.includes(suggestion) && (
                                <span
                                  className={
                                    selectedRating === "positive"
                                      ? "text-success"
                                      : "text-warning"
                                  }
                                >
                                  ✓
                                </span>
                              )}
                              {suggestion}
                            </span>
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Free text field */}
                    <Textarea
                      {...commentsProps}
                      classNames={{
                        base: "mt-4",
                        input:
                          "text-white placeholder:text-gray-400 min-h-[100px]",
                        inputWrapper:
                          "border-gray-600 hover:border-gray-400 focus-within:border-primary bg-slate-800/50",
                      }}
                      label="Any additional thoughts? (optional)"
                      labelPlacement="outside"
                      placeholder={
                        selectedRating === "positive"
                          ? "Share more about what you loved or any features you'd like to see improved..."
                          : "Tell us more about what didn't work or how we could make this better..."
                      }
                      variant="bordered"
                    />
                  </div>
                )}

                {/* Privacy notice */}
                <div className="bg-gray-500/5 border-l-4 border-gray-400 p-3 rounded">
                  <p className="text-xs text-gray-400">
                    <strong>Privacy Notice:</strong> Your feedback is anonymous
                    and helps us improve the product. We welcome all insights
                    and don&apos;t share feedback with third parties or use it
                    for marketing purposes.
                  </p>
                </div>
              </div>
            </ModalBody>

            <ModalFooter className="pt-6">
              <div className="flex gap-3 w-full">
                <Button
                  className="flex-1 bg-slate-700/50 hover:bg-slate-600/50 text-gray-300 font-medium py-3 rounded-lg transition-all duration-200 border border-slate-600/30"
                  variant="flat"
                  onPress={handleClose}
                >
                  Maybe later
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2"
                  isDisabled={!selectedRating}
                  type="submit"
                  variant="solid"
                >
                  <Send className="w-4 h-4" />
                  Send Feedback
                </Button>
              </div>
            </ModalFooter>
          </form>
        ) : (
          // Success state
          <>
            <ModalHeader className="flex flex-col gap-1 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                  <StarFill className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Thank you!</h2>
                  <p className="text-gray-400 text-sm">
                    Your feedback has been received
                  </p>
                </div>
              </div>
            </ModalHeader>

            <ModalBody className="py-6">
              <div className="text-center space-y-4">
                <p className="text-gray-300 text-lg">
                  {selectedRating === "positive"
                    ? "We're thrilled you enjoyed the experience! 🎉"
                    : "Thanks for the honest feedback - we'll work on improving! 💪"}
                </p>
                {selectedSuggestions.length > 0 && (
                  <div className="text-gray-400 text-sm">
                    <p className="mb-2">
                      You highlighted:{" "}
                      <strong>{selectedSuggestions.length}</strong> key points
                    </p>
                    <div className="flex flex-wrap gap-1 justify-center">
                      {selectedSuggestions.slice(0, 3).map((suggestion) => (
                        <span
                          key={suggestion}
                          className="px-2 py-1 bg-slate-700 rounded-full text-xs"
                        >
                          {suggestion}
                        </span>
                      ))}
                      {selectedSuggestions.length > 3 && (
                        <span className="px-2 py-1 bg-slate-700 rounded-full text-xs">
                          +{selectedSuggestions.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-gray-400 text-sm">
                  Your input helps us build better tools for the cybersecurity
                  community. Thank you for taking the time to share your
                  thoughts!
                </p>
                <p className="text-gray-500 text-xs">
                  This window will close automatically in a few seconds...
                </p>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
